import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import API_BASE from "config/api";
import Footer from "./Footer";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { sidebarLinks, secondaryLinks } from "./sidebarConfig";
import useBreakpoint from "utils/useBreakPoint";
import { getAccessToken, getRefreshToken, setTokens } from "utils/auth";

const Layout = () => {
  const { t } = useTranslation();
  const breakpoint = useBreakpoint();
  const location = useLocation();
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(false);
  const [pinned, setPinned] = useState(true);
  const [profile, setProfile] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  const allLinks = useMemo(() => [...sidebarLinks, ...secondaryLinks], []);
  const allowedModules = useMemo(
    () => (profile?.allowed_modules || []).map((m) => (m || "").toLowerCase()),
    [profile]
  );

  const resolveModuleForPath = useCallback(
    (pathname) => {
      const matchPath = (route = "") => {
        if (!route) return false;
        const cleaned = route.endsWith("/") && route !== "/" ? route.slice(0, -1) : route;
        return pathname === cleaned || pathname.startsWith(`${cleaned}/`);
      };
      for (const link of allLinks) {
        if (matchPath(link.route)) return (link.module || "").toLowerCase();
        for (const child of link.children || []) {
          const base = link.route || "";
          const childPath = child.route
            ? `${base.replace(/\/$/, "")}/${child.route.replace(/^\//, "")}`
            : base;
          if (matchPath(childPath)) return (link.module || "").toLowerCase();
        }
      }
      return null;
    },
    [allLinks]
  );

  useEffect(() => {
    let cancelled = false;

    const bootstrapAuth = async () => {
      const url = `${API_BASE}/api/auth/me`;
      const refresh = getRefreshToken();
      let activeAccess = getAccessToken();

      // Always try to refresh first so child routes mount with a fresh token
      if (refresh) {
        try {
          const refreshRes = await fetch(`${API_BASE}/api/auth/refresh`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refresh_token: refresh }),
          });
          if (refreshRes.ok) {
            const tokens = await refreshRes.json();
            const newAccess = tokens?.access_token;
            const newRefresh = tokens?.refresh_token || refresh;
            if (newAccess) {
              setTokens(newAccess, newRefresh);
              activeAccess = newAccess;
            }
          }
        } catch {
          /* ignore refresh errors */
        }
      }

      if (activeAccess) {
        try {
          const res = await fetch(url, {
            headers: { Authorization: `Bearer ${activeAccess}` },
            credentials: "include",
          });
          if (res?.ok) {
            const data = await res.json();
            if (!cancelled) setProfile(data);
          }
        } catch {
          /* ignore network errors */
        }
      }

      if (!cancelled) setAuthReady(true);
    };

    bootstrapAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!allowedModules.length) return;
    const moduleKey = resolveModuleForPath(location.pathname);
    if (!moduleKey || moduleKey === "logout") return;
    if (!allowedModules.includes(moduleKey)) {
      const fallback = allLinks.find((link) =>
        allowedModules.includes((link.module || "").toLowerCase())
      );
      const target = fallback?.route || "/news";
      if (location.pathname !== target) {
        navigate(target, { replace: true });
      }
    }
  }, [allowedModules, allLinks, location.pathname, navigate, resolveModuleForPath]);

  if (!authReady) {
    return (
      <div
        className="layout_loading"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: "#0c1017",
          color: "#f5f6f8",
        }}
      >
        <style>
          {`
            @keyframes df-spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              border: "2px solid #4B86F7",
              borderTopColor: "transparent",
              animation: "df-spin 0.9s linear infinite",
            }}
          />
          <span>{t("layout.preparingWorkspace")}</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="container-fluid px-0 ">
        <div
          className="main_content_wrap"
          onClick={() => {
            if (collapsed && breakpoint === "mobile") {
              setCollapsed(false);
            }
          }}
        >
          <div className={`sidebar_wrap ${collapsed || pinned ? "collapsed pin_sidebar" : ""}`}>
            <Sidebar onTogglePin={() => setPinned(!pinned)} pinned={pinned} profile={profile} />
          </div>
          <div className={`main_layout_content ${pinned ? "content_pinned" : "content_unpinned"}`}>
            <div className="header_wrap">
              <Header onToggleSidebar={() => setCollapsed(!collapsed)} />
            </div>
            <div className="layout_body">
              <Outlet />
            </div>
            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
